import React, { useState, useEffect, useCallback } from "react";
import { Input, Button, Table, Select, Typography, Avatar } from "antd";
import { useDispatch } from "react-redux";
import axios from "axios";
import Layout from "../components/layout";
import { showLoading, hideLoading } from "../redux/alertsSlice";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment-timezone";
import {
  SearchOutlined, FileExcelOutlined, FilePdfOutlined,
  FilterOutlined, ClearOutlined, CalendarOutlined,
  ClockCircleOutlined
} from "@ant-design/icons";

const { Title, Text } = Typography;

/* ─── Tokens ─────────────────────────────────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

/* ─── Status config ───────────────────────────────────────────── */
const STATUS_CONFIG = {
  pending:    { color: "#b45309", bg: "#fef9c3" },
  approved:   { color: EMERALD,   bg: TEAL_LIGHT },
  "follow-up":{ color: "#7c3aed", bg: "#f5f3ff"  },
  completed:  { color: "#16a34a", bg: "#dcfce7"  },
  rejected:   { color: "#dc2626", bg: "#fee2e2"  },
};
const getStatusCfg = (s) => STATUS_CONFIG[s] || { color: MUTED, bg: SURFACE };

/* ─── Style injection ─────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("schedule-appt-styles")) return;
  const el = document.createElement("style");
  el.id = "schedule-appt-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    .sa-root { font-family:'DM Sans',sans-serif; background:${SURFACE}; min-height:100vh; }
    .sa-wrap { max-width:1280px; margin:0 auto; padding:32px 24px 64px; }

    /* Hero */
    .sa-hero {
      background:linear-gradient(135deg,${SLATE} 0%,${SLATE_MID} 100%);
      border-radius:24px; padding:36px 44px;
      position:relative; overflow:hidden; margin-bottom:28px;
    }
    .sa-hero::before {
      content:''; position:absolute; top:-80px; right:-80px;
      width:300px; height:300px; border-radius:50%;
      background:radial-gradient(circle,${TEAL}44 0%,transparent 70%);
      filter:blur(32px); pointer-events:none;
    }
    .sa-hero::after {
      content:''; position:absolute; bottom:-60px; left:-40px;
      width:200px; height:200px; border-radius:50%;
      background:radial-gradient(circle,${EMERALD}33 0%,transparent 70%);
      filter:blur(26px); pointer-events:none;
    }
    .sa-eyebrow {
      display:inline-block; padding:4px 12px; border-radius:20px;
      background:${TEAL_LIGHT}; color:${EMERALD};
      font-size:11px; font-weight:700; letter-spacing:2px; text-transform:uppercase;
      font-family:'Sora',sans-serif; margin-bottom:12px;
    }
    .sa-hero-badges { display:flex; gap:8px; flex-wrap:wrap; margin-top:12px; }
    .sa-hero-badge {
      background:rgba(255,255,255,0.12); border-radius:20px;
      padding:4px 12px; font-size:12px; color:rgba(255,255,255,0.75);
      font-family:'DM Sans',sans-serif;
    }

    /* Toolbar */
    .sa-toolbar {
      background:${WHITE}; border-radius:16px;
      box-shadow:0 4px 20px rgba(0,0,0,.06);
      padding:16px 20px; margin-bottom:20px;
      display:flex; align-items:center; gap:12px; flex-wrap:wrap;
    }
    .sa-root .sa-search.ant-input-affix-wrapper {
      border-radius:10px !important; height:40px !important;
      border:1.5px solid #e2e8f0 !important; flex:1; min-width:180px;
      font-family:'DM Sans',sans-serif !important; font-size:13px !important;
      transition:border-color .2s,box-shadow .2s !important;
    }
    .sa-root .sa-search.ant-input-affix-wrapper:hover,
    .sa-root .sa-search.ant-input-affix-wrapper-focused {
      border-color:${TEAL} !important; box-shadow:0 0 0 3px ${TEAL}22 !important;
    }
    .sa-root .sa-search .ant-input { font-family:'DM Sans',sans-serif !important; font-size:13px !important; }

    .sa-root .sa-status-select .ant-select-selector {
      border-radius:10px !important; height:40px !important;
      border:1.5px solid #e2e8f0 !important;
      display:flex !important; align-items:center !important;
      font-family:'DM Sans',sans-serif !important; font-size:13px !important;
      transition:border-color .2s,box-shadow .2s !important;
    }
    .sa-root .sa-status-select .ant-select-selection-item,
    .sa-root .sa-status-select .ant-select-selection-placeholder { line-height:38px !important; }
    .sa-root .sa-status-select.ant-select-focused .ant-select-selector,
    .sa-root .sa-status-select .ant-select-selector:hover {
      border-color:${TEAL} !important; box-shadow:0 0 0 3px ${TEAL}22 !important;
    }

    .sa-btn {
      height:40px !important; padding:0 16px !important; border-radius:10px !important;
      font-size:13px !important; font-family:'DM Sans',sans-serif !important; font-weight:600 !important;
      border:1.5px solid #e2e8f0 !important; background:${WHITE} !important;
      color:${SLATE_MID} !important; transition:border-color .2s,color .2s !important;
      display:flex !important; align-items:center !important; gap:6px !important;
    }
    .sa-btn:hover         { border-color:${TEAL} !important; color:${TEAL} !important; }
    .sa-btn-excel:hover   { border-color:#16a34a !important; color:#16a34a !important; }
    .sa-btn-pdf:hover     { border-color:#dc2626 !important; color:#dc2626 !important; }

    /* Table */
    .sa-table-wrap {
      background:${WHITE}; border-radius:20px;
      box-shadow:0 4px 20px rgba(0,0,0,.06); overflow:hidden;
    }
    .sa-root .ant-table-thead > tr > th {
      background:${SURFACE} !important; color:${SLATE_MID} !important;
      font-size:11px !important; font-weight:700 !important;
      letter-spacing:.5px; text-transform:uppercase;
      border-bottom:1.5px solid #f1f5f9 !important;
      font-family:'Sora',sans-serif !important; padding:14px 16px !important;
    }
    .sa-root .ant-table-tbody > tr > td {
      font-size:13px !important; font-family:'DM Sans',sans-serif !important;
      color:${SLATE_MID} !important; padding:12px 16px !important;
      border-bottom:1px solid #f8fafc !important;
    }
    .sa-root .ant-table-tbody > tr:hover > td { background:#f0fdfb !important; }
    .sa-root .ant-pagination { padding:16px !important; }
    .sa-root .ant-pagination-item-active { border-color:${TEAL} !important; }
    .sa-root .ant-pagination-item-active a { color:${TEAL} !important; }

    /* Status badge */
    .sa-badge {
      padding:3px 10px; border-radius:20px;
      font-size:11px; font-weight:700; font-family:'Sora',sans-serif;
      display:inline-block;
    }

    /* Clinic day chip */
    .sa-day-chip {
      display:inline-block; padding:2px 8px; margin:2px;
      background:${TEAL_LIGHT}; color:${EMERALD};
      border-radius:6px; font-size:11px; font-weight:600;
    }

    /* Today date highlight in header */
    .sa-today-pill {
      background:rgba(20,184,166,0.2); border:1px solid ${TEAL}66;
      border-radius:20px; padding:4px 14px;
      font-size:12px; color:${TEAL}; font-family:'DM Sans',sans-serif;
      display:inline-block; margin-top:12px;
    }

    /* Animations */
    @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
    .sa-fade   { animation:fadeUp .5s ease both; }
    .sa-fade-1 { animation-delay:.05s; }
    .sa-fade-2 { animation-delay:.12s; }
  `;
  document.head.appendChild(el);
};

/* ─── Main component ──────────────────────────────────────────── */
const ScheduleAppointment = () => {
  const dispatch = useDispatch();
  const [appointments,   setAppointments]   = useState([]);
  const [filteredInfo,   setFilteredInfo]   = useState({});
  const [sortedInfo,     setSortedInfo]     = useState({});
  const [searchTerm,     setSearchTerm]     = useState("");
  const [statusFilter,   setStatusFilter]   = useState("All");

  useEffect(() => { injectStyles(); }, []);

  /* ── Exports ── */
  const exportToCSV = () => {
    const exportData = filteredData.map(a => ({
      Patient: a.userInfo?.name || "N/A",
      "Appointment Service": a.doctorInfo?.service || "N/A",
      "Doctor Name": `${a.doctorInfo?.firstName || ""} ${a.doctorInfo?.lastName || ""}`,
      "Clinic Day": a.doctorInfo?.clinicDay?.join(", ") || "N/A",
      "Scheduled Date": a.schedule_date ? moment(a.schedule_date).format("DD-MM-YYYY") : "Not set",
      Status: a.status,
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Appointments");
    saveAs(new Blob([XLSX.write(wb, { type: "array", bookType: "xlsx" })], { type: "application/octet-stream" }), "appointments.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    autoTable(doc, {
      head: [["Patient", "Service", "Doctor", "Clinic Day", "Date", "Status"]],
      body: filteredData.map(a => [
        a.userInfo?.name || "N/A",
        a.doctorInfo?.service || "N/A",
        `${a.doctorInfo?.firstName || ""} ${a.doctorInfo?.lastName || ""}`,
        a.doctorInfo?.clinicDay?.join(", ") || "N/A",
        a.schedule_date ? moment(a.schedule_date).format("DD-MM-YYYY") : "Not set",
        a.status,
      ]),
    });
    doc.save("appointments.pdf");
  };

  /* ── Data ── */
  const getAppointmentData = useCallback(async () => {
    try {
      dispatch(showLoading());
      const today = moment().tz("Africa/Lagos").format("YYYY-MM-DD");
      const res = await axios.get(
        `/api/user/get-all-appointments?date=${today}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      dispatch(hideLoading());
      if (res.data.success) setAppointments(res.data.data);
    } catch {
      dispatch(hideLoading());
    }
  }, [dispatch]);

  useEffect(() => { getAppointmentData(); }, [getAppointmentData]);

  const handleTableChange = (_, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const filteredData = appointments.filter(a => {
    const matchesSearch = a.userInfo?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "All" || a.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  /* ── Summary counts ── */
  const counts = appointments.reduce((acc, a) => {
    acc[a.status] = (acc[a.status] || 0) + 1;
    return acc;
  }, {});

  const todayLabel = moment().tz("Africa/Lagos").format("dddd, MMMM D, YYYY");

  /* ── Columns ── */
  const columns = [
    {
      title: "Patient",
      key: "patient",
      render: (_, r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar size={32} style={{ background: `linear-gradient(135deg,${EMERALD},${TEAL})`, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            {r.userInfo?.name?.[0]?.toUpperCase()}
          </Avatar>
          <Text strong style={{ fontSize: 13, color: SLATE }}>{r.userInfo?.name || "N/A"}</Text>
        </div>
      ),
      sorter: (a, b) => (a.userInfo?.name || "").localeCompare(b.userInfo?.name || ""),
      sortOrder: sortedInfo.columnKey === "patient" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Service",
      key: "service",
      render: (_, r) => <Text style={{ fontSize: 13, color: SLATE_MID }}>{r.doctorInfo?.service || "—"}</Text>,
      filters: [...new Set(appointments.map(a => a.doctorInfo?.service))].filter(Boolean).map(s => ({ text: s, value: s })),
      filteredValue: filteredInfo.service || null,
      onFilter: (value, record) => record.doctorInfo?.service === value,
      sorter: (a, b) => (a.doctorInfo?.service || "").localeCompare(b.doctorInfo?.service || ""),
      sortOrder: sortedInfo.columnKey === "service" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Doctor",
      key: "doctorName",
      render: (_, r) => (
        <Text style={{ fontSize: 13, color: SLATE_MID }}>
          Dr. {r.doctorInfo?.firstName || ""} {r.doctorInfo?.lastName || ""}
        </Text>
      ),
      sorter: (a, b) =>
        `${a.doctorInfo?.firstName || ""} ${a.doctorInfo?.lastName || ""}`.localeCompare(
          `${b.doctorInfo?.firstName || ""} ${b.doctorInfo?.lastName || ""}`
        ),
      sortOrder: sortedInfo.columnKey === "doctorName" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Location",
      key: "location",
      render: (_, r) => <Text style={{ fontSize: 13, color: SLATE_MID }}>{r.doctorInfo?.location || "—"}</Text>,
      filters: [...new Set(appointments.map(a => a.doctorInfo?.location))].filter(Boolean).map(s => ({ text: s, value: s })),
      filteredValue: filteredInfo.location || null,
      onFilter: (value, record) => record.doctorInfo?.location === value,
      sorter: (a, b) => (a.doctorInfo?.location || "").localeCompare(b.doctorInfo?.location || ""),
      sortOrder: sortedInfo.columnKey === "location" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Clinic Days",
      key: "clinicDay",
      render: (_, r) => (
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          {r.doctorInfo?.clinicDay?.map((d, i) => (
            <span key={i} className="sa-day-chip">{d}</span>
          )) || <Text style={{ color: MUTED, fontSize: 12 }}>—</Text>}
        </div>
      ),
      sorter: (a, b) =>
        (a.doctorInfo?.clinicDay || []).join(", ").localeCompare((b.doctorInfo?.clinicDay || []).join(", ")),
      sortOrder: sortedInfo.columnKey === "clinicDay" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Scheduled Date",
      key: "schedule_date",
      render: (_, r) =>
        (r.status === "approved" || r.status === "follow-up") && r.schedule_date ? (
          <Text style={{ fontSize: 12, color: SLATE_MID }}>
            {moment(r.schedule_date).format("ddd, MMM D, YYYY")}
          </Text>
        ) : (
          <Text style={{ fontSize: 12, color: MUTED }}>—</Text>
        ),
      sorter: (a, b) => moment(a.schedule_date || 0).unix() - moment(b.schedule_date || 0).unix(),
      sortOrder: sortedInfo.columnKey === "schedule_date" ? sortedInfo.order : null,
    },
    {
      title: "Status",
      key: "status",
      render: (_, r) => {
        const cfg = getStatusCfg(r.status);
        return (
          <span className="sa-badge" style={{ background: cfg.bg, color: cfg.color }}>
            {r.status?.charAt(0).toUpperCase() + r.status?.slice(1)}
          </span>
        );
      },
      filters: [
        { text: "Pending",   value: "pending"   },
        { text: "Approved",  value: "approved"  },
        { text: "Rejected",  value: "rejected"  },
        { text: "Follow-up", value: "follow-up" },
        { text: "Completed", value: "completed" },
      ],
      filteredValue: filteredInfo.status || null,
      onFilter: (value, record) => record.status === value,
      width: 120,
    },
  ];

  return (
    <Layout>
      <div className="sa-root">
        <div className="sa-wrap">

          {/* Hero */}
          <div className="sa-hero sa-fade">
            <div style={{ position: "relative", zIndex: 1 }}>
              <div className="sa-eyebrow"><CalendarOutlined style={{ marginRight: 5 }} />Daily Schedule</div>
              <Title level={2} style={{ margin: "6px 0 4px", fontFamily: "'Sora',sans-serif", color: WHITE, fontWeight: 800 }}>
                Today's Appointments
              </Title>
              <Text style={{ color: MUTED, fontSize: 14 }}>
                All appointments scheduled for today across all locations and services
              </Text>
              <div className="sa-hero-badges">
                <div className="sa-hero-badge"><CalendarOutlined style={{ marginRight: 5 }} />{appointments.length} Total Today</div>
                {counts.approved  && <div className="sa-hero-badge" style={{ background: `${TEAL_LIGHT}cc`, color: EMERALD }}>✓ {counts.approved} Approved</div>}
                {counts.pending   && <div className="sa-hero-badge" style={{ background: "#fef9c3cc", color: "#b45309" }}>⏳ {counts.pending} Pending</div>}
                {counts.completed && <div className="sa-hero-badge" style={{ background: "#dcfce7cc", color: "#16a34a" }}>✔ {counts.completed} Completed</div>}
              </div>
              <div className="sa-today-pill">
                <ClockCircleOutlined style={{ marginRight: 6 }} />{todayLabel}
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <div className="sa-toolbar sa-fade sa-fade-1">
            <Input
              className="sa-search"
              prefix={<SearchOutlined style={{ color: MUTED }} />}
              placeholder="Search patient..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              allowClear
            />
            <Select
              className="sa-status-select"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 160 }}
              options={[
                { value: "All",        label: "All Statuses" },
                { value: "pending",    label: "Pending"      },
                { value: "approved",   label: "Approved"     },
                { value: "rejected",   label: "Rejected"     },
                { value: "follow-up",  label: "Follow-up"    },
                { value: "completed",  label: "Completed"    },
              ]}
            />
            <Button className="sa-btn sa-btn-excel" icon={<FileExcelOutlined />} onClick={exportToCSV}>Excel</Button>
            <Button className="sa-btn sa-btn-pdf"   icon={<FilePdfOutlined />}   onClick={exportToPDF}>PDF</Button>
            <Button className="sa-btn" icon={<FilterOutlined />} onClick={() => setFilteredInfo({})}>Filters</Button>
            <Button className="sa-btn" icon={<ClearOutlined />}  onClick={() => { setFilteredInfo({}); setSortedInfo({}); }}>Reset</Button>
          </div>

          {/* Table */}
          <div className="sa-table-wrap sa-fade sa-fade-2">
            <Table
              columns={columns}
              dataSource={filteredData.map(a => ({ ...a, key: a._id }))}
              onChange={handleTableChange}
              scroll={{ x: "max-content" }}
              pagination={{ pageSize: 10, showSizeChanger: false }}
            />
          </div>

        </div>
      </div>
    </Layout>
  );
};

export default ScheduleAppointment;